import AppHeader from '@components/AppNavigation/AppHeader';
import { ActionsFooter, ApprovalDialogButton } from '@components/Forms';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { LoadingButton } from '@mui/lab';
import { Box, Grid, IconButton, useTheme } from '@mui/material';
import { onSubmitForm } from '@utils/forms';
import { useFormik } from 'formik';
import { useToast } from 'hooks';
import React, { FC } from 'react';
import { OAuthProviders } from 'types/auth';
import {
	ISocialProviderSettingsWrapper,
	ISocialSettings
} from 'types/settings';
import { ITheme } from 'types/theme';
import AuthMethodSwitch from './AuthMethodSwitch';
import SocialAccountSetUp from './Social/SocialAccountSetup';
import SocialCard from './Social/SocialCard';

interface ISocialProps {
	settings?: ISocialSettings;
	onSocialSave: (data: ISocialSettings) => Promise<ISocialSettings>;
}

const DEFAULT_FORM: ISocialSettings = {
	id: '',
	version: 0,
	enabled: false
};

const SocialPage: FC<ISocialProps> = ({ settings, onSocialSave }) => {
	const theme = useTheme<ITheme>();
	const [socialSetUp, setSocialSetUp] = React.useState<
		OAuthProviders | undefined
	>(undefined);

	const handleShowSocialSetup = React.useCallback(
		(socialName: OAuthProviders) => {
			setSocialSetUp(socialName);
		},
		[setSocialSetUp]
	);

	const handleSaveSocial = React.useCallback(
		async (data: ISocialProviderSettingsWrapper) => {
			if (!settings!.providerSettings) {
				settings!.providerSettings = {};
			}
			settings!.providerSettings[socialSetUp!] = data;
			settings!.callbackDomain = data.callbackDomain;
			delete data.callbackDomain;
			delete settings!.callbackUrl;
			const res = await onSocialSave(settings!);
			return res.providerSettings![socialSetUp!];
		},
		[settings, onSocialSave, socialSetUp]
	);

	const handleHideSocialSetup = React.useCallback(() => {
		setSocialSetUp(undefined);
	}, [setSocialSetUp]);

	const { addToast } = useToast();

	const formik = useFormik<ISocialSettings>({
		enableReinitialize: true,
		initialValues: {
			...DEFAULT_FORM,
			...settings,
			callbackDomain: settings?.callbackDomain || ''
		},
		onSubmit: onSubmitForm<ISocialSettings>(onSocialSave, addToast)
	});

	return (
		<Box
			display="flex"
			flexDirection="column"
			flexGrow={1}
			data-testid="authentication-methods-page-social"
			overflow="hidden"
		>
			<Box display="flex" alignItems="center" alignContent="center">
				{socialSetUp && (
					<Box display="flex" ml={2}>
						<IconButton
							color="primary"
							aria-label={intl.tr('button.back')}
							component="span"
							onClick={handleHideSocialSetup}
							data-testid="social-login-back-button"
						>
							<ArrowBackIcon />
						</IconButton>
					</Box>
				)}
				<AppHeader
					mb={socialSetUp ? 2 : 0}
					translation={
						socialSetUp
							? `socials.${socialSetUp}`
							: 'pages.authMethods.social.title'
					}
				/>
			</Box>

			{!socialSetUp && (
				<Box ml={4}>
					<AuthMethodSwitch formik={formik} />
				</Box>
			)}

			{socialSetUp ? (
				<SocialAccountSetUp
					data={
						settings?.providerSettings
							? settings?.providerSettings[socialSetUp]
							: undefined
					}
					onSave={handleSaveSocial}
					callbackUrl={settings?.callbackUrl || ''}
					callbackDomain={settings?.callbackDomain || ''}
					globalEnabled={settings?.enabled || false}
				/>
			) : (
				<>
					<Box
						display="flex"
						overflow="auto"
						flexDirection="column"
						flexGrow={1}
					>
						<Grid container spacing={2} p={4}>
							<SocialCard
								logo={theme.assets.socials.Google()}
								clientId={settings?.providerSettings?.google?.clientID}
								socialName={OAuthProviders.google}
								onClick={handleShowSocialSetup}
							/>
							<SocialCard
								logo={theme.assets.socials.Github()}
								clientId={settings?.providerSettings?.github?.clientID}
								onClick={handleShowSocialSetup}
								socialName={OAuthProviders.github}
							/>
							<SocialCard
								logo={theme.assets.socials.Microsoft()}
								clientId={settings?.providerSettings?.microsoft?.clientID}
								onClick={handleShowSocialSetup}
								socialName={OAuthProviders.microsoft}
							/>
							<SocialCard
								logo={theme.assets.socials.Apple()}
								clientId={settings?.providerSettings?.apple?.clientID}
								socialName={OAuthProviders.apple}
								onClick={handleShowSocialSetup}
							/>
							<SocialCard
								logo={theme.assets.socials.Facebook()}
								clientId={settings?.providerSettings?.facebook?.clientID}
								socialName={OAuthProviders.facebook}
								onClick={handleShowSocialSetup}
							/>
							<SocialCard
								logo={theme.assets.socials.Gitlab()}
								clientId={settings?.providerSettings?.gitlab?.clientID}
								socialName={OAuthProviders.gitlab}
								onClick={handleShowSocialSetup}
							/>
							<SocialCard
								logo={theme.assets.socials.Discord()}
								clientId={settings?.providerSettings?.discord?.clientID}
								socialName={OAuthProviders.discord}
								onClick={handleShowSocialSetup}
							/>
							<SocialCard
								logo={theme.assets.socials.Linkedin()}
								clientId={settings?.providerSettings?.linkedin?.clientID}
								socialName={OAuthProviders.linkedin}
								onClick={handleShowSocialSetup}
							/>
						</Grid>
					</Box>
					<ActionsFooter>
						<LoadingButton
							color="primary"
							variant="contained"
							onClick={() => formik.handleSubmit()}
							loading={formik.isSubmitting}
							disabled={!formik.dirty}
							sx={{ marginRight: 1 }}
						>
							{intl.tr('button.save')}
						</LoadingButton>
						<ApprovalDialogButton
							buttonText={intl.tr('button.cancel')}
							onApprove={formik.resetForm}
							disabled={!formik.dirty}
							title={intl.tr('discard.changes.dialog.title')}
							question={intl.tr('discard.changes.dialog.question')}
							approveText={intl.tr('discard.changes.dialog.approve')}
						/>
					</ActionsFooter>
				</>
			)}
		</Box>
	);
};

SocialPage.defaultProps = {
	settings: { id: '', version: 0, enabled: false, providerSettings: {} }
};

export default SocialPage;
